import { create } from 'zustand';
import { platformDetector, Platform } from '../services/platformDetector';
import { apiClient } from '../services/apiClient';

export interface UserProfile {
  id: string | number;
  name: string;
  phone?: string;
  email?: string;
  branchId?: string;
  patientClinicId?: string;
  points?: number;
  clinicId?: string;
  chatId?: string | number;
}

interface AuthState {
  isLoading: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
  platform: Platform | null;
  user: UserProfile | null;
  isMatched: boolean;
  error: string | null;
  branchVersion: number;

  initialize: () => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setUser: (user: UserProfile) => void;
  bumpBranchVersion: () => void;
}

/**
 * Ждём загрузки SDK (Telegram / MAX)
 */
const waitForSDK = async (timeout = 5000): Promise<void> => {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    const hasTelegram = !!window.Telegram?.WebApp;
    const hasMax = !!window.WebApp;

    if (hasTelegram || hasMax) return;

    await new Promise((r) => setTimeout(r, 50));
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  isLoading: false,
  isInitialized: false,
  isAuthenticated: false,
  platform: null,
  user: null,
  isMatched: false,
  error: null,
  branchVersion: 0,

  initialize: async () => {
    if (get().isLoading) return;

    set({ isLoading: true, error: null, isInitialized: false });

    try {
      if (typeof window === 'undefined') {
        throw new Error('Нет window (SSR)');
      }

      // 1. Ждём SDK → убираем race condition
      await waitForSDK();

      // 2. Определяем платформу (единый источник истины)
      const platform = platformDetector.detect();

      if (platform === Platform.UNKNOWN) {
        throw new Error('Мини-приложение доступно только в Telegram или MAX');
      }

      // 3. Инициализация SDK
      if (platform === Platform.TELEGRAM) {
        window.Telegram?.WebApp?.ready?.();
        window.Telegram?.WebApp?.expand?.();
      }

      if (platform === Platform.MAX) {
        window.WebApp?.ready?.();
      }

      // 4. Проверяем initData (НЕ для детекта, а для валидности)
      const initData =
        platform === Platform.TELEGRAM
          ? window.Telegram?.WebApp?.initData
          : window.WebApp?.initData;

      if (!initData) {
        throw new Error('Мини-приложение доступно только в Telegram или MAX');
      }

      // 5. Запрос пользователя
      const data = await apiClient.get<{
        user: UserProfile;
        isMatched: boolean;
      }>('/webapp/user/me');

      set({
        isAuthenticated: true,
        isInitialized: true,
        platform,
        user: data.user,
        isMatched: data.isMatched || false,
        isLoading: false,
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Ошибка инициализации';

      console.error('[Auth] Initialize error:', errorMessage);

      if (process.env.NODE_ENV === 'development') {
        console.log('[Auth Debug]', {
          detectedPlatform: window.detectedPlatform,
          telegram: !!window.Telegram?.WebApp,
          max: !!window.WebApp,
          telegramInitData: !!window.Telegram?.WebApp?.initData,
          maxInitData: !!window.WebApp?.initData,
          userAgent: navigator.userAgent,
        });
      }

      set({
        error: errorMessage,
        isAuthenticated: false,
        isInitialized: true,
        isLoading: false,
      });
    }
  },

  logout: () => {
    set({
      isAuthenticated: false,
      platform: null,
      user: null,
      isMatched: false,
      error: null,
      isInitialized: false,
      isLoading: false,
    });
  },

  clearError: () => set({ error: null }),

  setUser: (user: UserProfile) => set({ user }),

  bumpBranchVersion: () =>
    set((state) => ({ branchVersion: state.branchVersion + 1 })),
}));